/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { LanguageService } from "app/core/services/language/language.service";
import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { RoleService } from "app/core/services/role/role.service";
import { AuthService } from "app/auth/services/auth.service";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from "@angular/common";
import { filter, Subscription } from "rxjs";
import { User } from "app/core/models/user";
import { Offcanvas } from 'bootstrap';

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [ CommonModule, TranslateModule ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss'
})

export class HeaderComponent implements OnInit, OnDestroy {

	public user: any;

    public isAdmin: boolean = false;

    public selectedRole: string = '';

    public activeRoute: string = '';

	private subscription?: Subscription;

	public isDropdownOpen: boolean = false;
	public isDropdownClosing: boolean = false;

	private closeDropdownTimeout?: ReturnType<typeof setTimeout>;

	private static readonly USER_DROPDOWN_ANIM_MS = 300;
	private static readonly LANGUAGE_OFFCANVAS_ID = 'offcanvasScrolling';

	public languages: { code: string, label: string, flagcode: string }[] = [
		{ code: 'es', label: 'Español', flagcode: 'es' },
		{ code: 'en', label: 'English', flagcode: 'gb' },
		{ code: 'eu', label: 'Euskera', flagcode: 'es-pv' }
	];

    constructor(
		private laguageService: LanguageService,
		private roleService: RoleService,
		private authService: AuthService,
        private router: Router
    ) { }

    ngOnInit(): void { 
		this.obtainProfile();
		this.isAdmin = this.isAdministrator();

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd)
			)
			.subscribe((event: NavigationEnd) => {
				const url = event.urlAfterRedirects || event.url;
				this.setActiveRoute(url);
				this.syncHeaderTabFromUrl(url);
			});

		this.subscription = this.roleService.currentRole$.subscribe(() => {
			this.syncHeaderTabFromUrl(this.router.url);
		});

		this.syncHeaderTabFromUrl(this.router.url);
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.clearCloseDropdownTimeout();
	}

	public get isDropdownMenuMounted(): boolean {
		return this.isDropdownOpen || this.isDropdownClosing;
	}

    private async obtainProfile(): Promise<void> {
		try {
			const profile = await this.authService.getUserProfile();
			if (!profile) {
				console.error('No profile returned from getUserProfile()');
				this.user = User.DEFAULT;
				return;
			}
			if (!profile.id && !profile.username) {
				console.error('Profile is missing id and username', profile);
			}
			this.user = new User(
				profile.id ?? User.DEFAULT.ID,
				profile.username ?? User.DEFAULT.username,
				profile.firstName ?? User.DEFAULT.name,
				profile.lastName ?? User.DEFAULT.surname,
				profile.email ?? User.DEFAULT.email,
				profile.emailVerified ?? User.DEFAULT.emailVerified
			);
		} catch (err) {
			console.error('Error getting user profile in obtainProfile()', err);
			this.user = User.DEFAULT;
		}
	}

    private isAdministrator(): boolean {
		return this.authService.checkRealmRolesIncludeAnyOfRequiredRoles(['ds-catalog-administrator']);
	}

    private setActiveRoute(url: string): void {
		const routeMap: Record<string, string> = {
			'/administration': 'administration',
			'/catalog': 'catalog'
		};

		const match = Object.entries(routeMap).find(([path]) => url.includes(path));
		this.activeRoute = match ? match[1] : '';
	}

	private syncHeaderTabFromUrl(url: string): void {
		const path = url.split('?')[0];
		if (path.includes('/administration')) {
			this.selectedRole = 'Administrator';
		} else if (path.includes('/catalog')) {
			this.selectedRole = 'Catalog';
		} else if (path.includes('/role')) {
			this.selectedRole = '';
		}
	}

	public navigateTo(path: string): void {
		const parts = this.router.url.split('?')[0].split('/').filter(Boolean);
		const currentChild = parts.length >= 2 ? parts[1] : '';
		if (currentChild !== path) {
			void this.router.navigate(['home', path]);
		}
	}

	public toggleDropdown(): void {
		if (this.isDropdownClosing) {
			return;
		}
		if (this.isDropdownOpen) {
			this.closeDropdownAnimated();
		} else {
			this.isDropdownOpen = true;
		}
	}

	@HostListener('document:click', ['$event'])
	public closeFloatingMenusOnClickOutside(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		if (!target.closest('.dropdown') && this.isDropdownOpen && !this.isDropdownClosing) {
			this.closeDropdownAnimated();
		}
		this.closeLanguageOffcanvasOnClickOutside(target);
	}

	public changeLang(lang: string): void {
		this.laguageService.changeLang(lang);
	}

	public get currentLang(): string {
		return this.laguageService.getCurrentLang();
	}

	public logout(): void {
		if (this.isDropdownOpen && !this.isDropdownClosing) {
			this.closeDropdownAnimated(() => this.authService.logout());
		} else {
			this.authService.logout();
		}
	}

	private closeDropdownAnimated(onFullyClosed?: () => void): void {
		if (!this.isDropdownOpen || this.isDropdownClosing) {
			return;
		}
		this.isDropdownClosing = true;
		this.clearCloseDropdownTimeout();
		this.closeDropdownTimeout = setTimeout(() => {
			this.isDropdownOpen = false;
			this.isDropdownClosing = false;
			this.closeDropdownTimeout = undefined;
			onFullyClosed?.();
		}, HeaderComponent.USER_DROPDOWN_ANIM_MS);
	}

	private clearCloseDropdownTimeout(): void {
		if (this.closeDropdownTimeout !== undefined) {
			clearTimeout(this.closeDropdownTimeout);
			this.closeDropdownTimeout = undefined;
		}
	}

	private closeLanguageOffcanvasOnClickOutside(target: HTMLElement): void {
		const languageOffcanvas = document.getElementById(HeaderComponent.LANGUAGE_OFFCANVAS_ID);
		if (!languageOffcanvas?.classList.contains('show')) {
			return;
		}

		const clickedInsideOffcanvas = target.closest('.language-offcanvas');
		const clickedLanguageTrigger = target.closest(`[data-bs-target="#${HeaderComponent.LANGUAGE_OFFCANVAS_ID}"]`);
		if (clickedInsideOffcanvas || clickedLanguageTrigger) {
			return;
		}

		Offcanvas.getInstance(languageOffcanvas)?.hide();
	}

}